// Active-ability behavior for the HIDDEN variant (/hidden) - the kids' reworked kit. Same
// framework as the base AbilitySystem (cooldowns, active timers, derived player buffs) but
// with its own effects: orbiting storks, a water burst, a silence note-burst, a dropped
// black hole, mass confusion, a boulder trap-ring and an OverLord buff. Reads the hidden
// roster + tuning from balanceHidden; shares the enemy hash, dealDamage and fx helpers.

import { ABILITIES_HIDDEN as ABILITIES, ABILITY_HIDDEN as ABILITY } from './balanceHidden';
import { MAX_ENEMY_RADIUS } from './balance';
import { dealDamage } from './combat';
import type { World } from './world';
import type { Boulder } from './boulders';
import type { Enemy } from './enemies';

export class AbilitySystemHidden {
  /** Cooldown remaining per ability (seconds); 0 = ready. */
  readonly cd: number[];
  /** Active-buff time remaining per ability (seconds). */
  readonly active: number[];
  private readonly tickAcc: number[]; // periodic-effect accumulators
  private readonly buf: number[] = []; // scratch for area queries

  // Derived per-tick player buffs (read by the World).
  dmgMul = 1;
  dmgMulClose = 1;
  closeRadius2 = 0;
  speedMul = 1;
  invuln = false;
  damageTakenMul = 1;
  damageFieldMul = 1;
  glowColor = -1; // color of the active buff aura, or -1
  sizeMul = 1; // player render scale (OverLord doubles it)
  readonly arenaFields: readonly never[] = [];
  readonly arenaAgents: readonly never[] = [];

  // Visual state read by the renderer (only on /hidden).
  storkActive = false;
  storkSpin = 0;
  readonly storkCount: number;
  readonly storkRingRadius: number;
  readonly storkOrbRadius: number;
  bhActive = false; // Back Hole marker sphere
  bhX = 0;
  bhY = 0;
  noteTimer = 0; // Trans Mute silence burst
  noteX = 0;
  noteY = 0;

  private readonly iBerserk: number;
  private readonly iBlackhole: number;

  constructor() {
    const n = ABILITIES.length;
    this.cd = new Array(n).fill(0);
    this.active = new Array(n).fill(0);
    this.tickAcc = new Array(n).fill(0);
    this.iBerserk = ABILITIES.findIndex((a) => a.key === 'berserk');
    this.iBlackhole = ABILITIES.findIndex((a) => a.key === 'blackhole');
    this.storkCount = ABILITY.storkCount;
    this.storkRingRadius = ABILITY.storkRingRadius;
    this.storkOrbRadius = ABILITY.storkOrbRadius;
  }

  isUnlocked(i: number, level: number): boolean {
    return level >= ABILITIES[i].unlockLevel;
  }

  onEnemyKilled(_enemy: Enemy, _world: World): void {}

  onContactHit(_enemy: Enemy, _world: World): void {}

  /** Activate the ability bound to `code` if unlocked + off cooldown. */
  activateByHotkey(code: string, level: number, world: World): boolean {
    for (let i = 0; i < ABILITIES.length; i++) {
      if (ABILITIES[i].hotkey === code) return this.activate(i, level, world);
    }
    return false;
  }

  activate(i: number, level: number, world: World): boolean {
    const def = ABILITIES[i];
    if (def === undefined || (level < def.unlockLevel && !world.allAbilities) || this.cd[i] > 0) return false;
    this.cd[i] = def.cooldownSec;
    this.active[i] = def.durationSec; // 0 for instant
    this.tickAcc[i] = 0;
    this.castFx(i, world);
    this.fire(i, world);
    return true;
  }

  /** Tick cooldowns + active timers, run ongoing/expiry effects, recompute buffs + visuals. */
  update(dt: number, world: World): void {
    this.storkSpin += ABILITY.storkSpinSpeed * dt;
    for (let i = 0; i < ABILITIES.length; i++) {
      if (this.cd[i] > 0) this.cd[i] -= dt;
      if (this.active[i] > 0) {
        this.active[i] -= dt;
        this.tickActive(i, dt, world);
        if (this.active[i] <= 0) this.onExpire(i, world);
      }
    }
    this.recomputeBuffs();
    this.storkActive = this.active[this.iBerserk] > 0;
    this.bhActive = this.active[this.iBlackhole] > 0;
    if (this.noteTimer > 0) this.noteTimer -= dt;
  }

  private recomputeBuffs(): void {
    this.dmgMul = 1;
    this.dmgMulClose = 1;
    this.closeRadius2 = 0;
    this.speedMul = 1;
    this.invuln = false;
    this.glowColor = -1;
    this.sizeMul = 1;
    for (let i = 0; i < ABILITIES.length; i++) {
      if (this.active[i] <= 0) continue;
      switch (ABILITIES[i].key) {
        case 'aegis':
          this.invuln = true;
          if (this.glowColor < 0) this.glowColor = ABILITIES[i].color;
          break;
        case 'tempest':
          this.speedMul = ABILITY.tempSpeedMul;
          if (this.glowColor < 0) this.glowColor = ABILITIES[i].color;
          break;
        case 'overload':
          this.invuln = true;
          this.dmgMul = Math.max(this.dmgMul, ABILITY.overlordDmgMul);
          this.sizeMul = ABILITY.overlordSizeMul;
          this.glowColor = ABILITIES[i].color;
          break;
      }
    }
  }

  // Instant effects fired on activation.
  private fire(i: number, world: World): void {
    const p = world.player;
    switch (ABILITIES[i].key) {
      case 'cryo': {
        const dmg = ABILITY.cryoDmgMul * this.strongest(world);
        this.damageAt(world, p.x, p.y, ABILITY.cryoRadius, dmg);
        this.slowAt(world, p.x, p.y, ABILITY.cryoRadius, ABILITY.cryoSlowSec, ABILITY.cryoSlowMul);
        break;
      }
      case 'transmute':
        this.damageAt(world, p.x, p.y, ABILITY.transmuteRadius, ABILITY.transmuteDamage);
        this.noteTimer = 0.5;
        this.noteX = p.x;
        this.noteY = p.y;
        break;
      case 'aegis':
        p.hp = Math.min(p.maxHp, p.hp + ABILITY.aegisHeal);
        this.knockback(world, ABILITY.aegisKnockRadius, ABILITY.aegisKnockback);
        break;
      case 'blackhole':
        this.bhX = p.x;
        this.bhY = p.y;
        this.bhActive = true;
        break;
      case 'tempest':
        this.confuseAll(world);
        break;
      case 'meltdown':
        world.setTrapBoulders(this.buildRing(p.x, p.y));
        break;
      // berserk (storks) + overload act over their duration.
    }
  }

  // Ongoing effects each tick while active.
  private tickActive(i: number, dt: number, world: World): void {
    switch (ABILITIES[i].key) {
      case 'berserk': {
        // Storks: each orbiting bird strikes foes it passes over, on a fixed cadence.
        this.tickAcc[i] += dt;
        while (this.tickAcc[i] >= ABILITY.storkTickSec) {
          this.tickAcc[i] -= ABILITY.storkTickSec;
          const dmg = ABILITY.storkDmgMul * this.strongest(world);
          const p = world.player;
          for (let k = 0; k < this.storkCount; k++) {
            const a = this.storkSpin + (k / this.storkCount) * Math.PI * 2;
            const sx = p.x + Math.cos(a) * this.storkRingRadius;
            const sy = p.y + Math.sin(a) * this.storkRingRadius;
            this.damageAt(world, sx, sy, this.storkOrbRadius, dmg);
          }
        }
        break;
      }
      case 'blackhole': {
        // Sit for the drop delay, then pull enemies toward the dropped hole.
        const elapsed = ABILITIES[i].durationSec - this.active[i];
        if (elapsed >= ABILITY.bhDropDelay) {
          this.pullTo(world, this.bhX, this.bhY, ABILITY.bhRadius, ABILITY.bhPullPerSec * dt);
        }
        break;
      }
    }
  }

  // End-of-duration effects.
  private onExpire(i: number, world: World): void {
    switch (ABILITIES[i].key) {
      case 'blackhole':
        world.fxRing(this.bhX, this.bhY, ABILITY.bhBoomRadius, ABILITIES[i].color);
        world.fxNova(this.bhX, this.bhY, ABILITIES[i].color, 30, 200, 380);
        this.damageAt(world, this.bhX, this.bhY, ABILITY.bhBoomRadius, ABILITY.bhBoomDamage);
        this.bhActive = false;
        break;
      case 'meltdown':
        world.setTrapBoulders([]);
        break;
    }
  }

  // Cast blast: expanding ring + particle nova, themed per ability.
  private castFx(i: number, world: World): void {
    const p = world.player;
    const def = ABILITIES[i];
    switch (def.key) {
      case 'berserk':
        world.fxRing(p.x, p.y, this.storkRingRadius, def.color);
        world.fxNova(p.x, p.y, 0xffffff, 24, 140, 260);
        break;
      case 'cryo':
        world.fxRing(p.x, p.y, ABILITY.cryoRadius * 0.6, 0x4fb0e0, 0.5);
        world.fxNova(p.x, p.y, 0x4fb0e0, 40, 200, 380);
        break;
      case 'transmute':
        world.fxRing(p.x, p.y, ABILITY.transmuteRadius, def.color);
        world.fxNova(p.x, p.y, def.color, 30, 160, 320);
        break;
      case 'aegis':
        world.fxRing(p.x, p.y, ABILITY.aegisKnockRadius, def.color);
        world.fxNova(p.x, p.y, def.color, 28, 160, 300);
        break;
      case 'blackhole':
        world.fxImplosion(p.x, p.y, def.color, 30, 210);
        break;
      case 'tempest':
        world.fxRing(p.x, p.y, 180, def.color);
        world.fxNova(p.x, p.y, def.color, 26, 140, 280);
        break;
      case 'meltdown':
        world.fxRing(p.x, p.y, ABILITY.ringRadius, 0xb08050, 0.5);
        world.fxNova(p.x, p.y, 0xb08050, 30, 160, 300);
        break;
      case 'overload':
        world.fxRing(p.x, p.y, 140, def.color);
        world.fxNova(p.x, p.y, def.color, 30, 160, 320);
        break;
    }
  }

  // --- Effect helpers ---

  /** 2x/3x "your regular damage" resolves to the strongest owned weapon's per-hit damage. */
  private strongest(world: World): number {
    const d = world.weapons.maxHitDamage(world.hitCtx.mods.damageMul);
    return d > 0 ? d : 10;
  }

  /** A ring of boulders around (cx,cy) - enough to overlap into a solid, trapping wall. */
  private buildRing(cx: number, cy: number): Boulder[] {
    const out: Boulder[] = [];
    const n = ABILITY.ringBoulders;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2;
      out.push({
        x: cx + Math.cos(a) * ABILITY.ringRadius,
        y: cy + Math.sin(a) * ABILITY.ringRadius,
        radius: ABILITY.ringBoulderRadius,
      });
    }
    return out;
  }

  /** Confuse + slow every active enemy (Tâmpești). */
  private confuseAll(world: World): void {
    const en = world.enemies;
    for (let i = 0; i < en.count; i++) {
      const e = en.items[i];
      if (e.hp <= 0) continue;
      e.confusedSec = Math.max(e.confusedSec, ABILITY.confuseSec);
      e.wanderAng = (e.x * 0.13 + e.y * 0.29) % (Math.PI * 2); // deterministic scattered heading
      e.slowSec = Math.max(e.slowSec, ABILITY.confuseSec);
      e.slowMul = Math.min(e.slowMul, ABILITY.confuseSlowMul);
    }
  }

  private damageAt(world: World, cx: number, cy: number, radius: number, dmg: number): void {
    const n = world.hash.query(cx, cy, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0) continue;
      const dx = e.x - cx;
      const dy = e.y - cy;
      if (dx * dx + dy * dy <= r2) dealDamage(world.hitCtx, e, dmg, e.x, e.y, false);
    }
  }

  private slowAt(world: World, cx: number, cy: number, radius: number, slowSec: number, slowMul: number): void {
    const n = world.hash.query(cx, cy, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0) continue;
      const dx = e.x - cx;
      const dy = e.y - cy;
      if (dx * dx + dy * dy <= r2) {
        e.slowSec = Math.max(e.slowSec, slowSec);
        e.slowMul = Math.min(e.slowMul, slowMul);
      }
    }
  }

  private knockback(world: World, radius: number, force: number): void {
    const p = world.player;
    const n = world.hash.query(p.x, p.y, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0) continue;
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= r2 && d2 > 0.001) {
        const d = Math.sqrt(d2);
        e.x += (dx / d) * force;
        e.y += (dy / d) * force;
      }
    }
  }

  private pullTo(world: World, cx: number, cy: number, radius: number, step: number): void {
    const n = world.hash.query(cx, cy, radius + MAX_ENEMY_RADIUS, this.buf);
    const r2 = radius * radius;
    for (let j = 0; j < n; j++) {
      const e = world.enemies.items[this.buf[j]];
      if (e.hp <= 0 || e.isBoss) continue; // don't yank bosses around
      const dx = cx - e.x;
      const dy = cy - e.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= r2 && d2 > 1) {
        const d = Math.sqrt(d2);
        const s = Math.min(step, d);
        e.x += (dx / d) * s;
        e.y += (dy / d) * s;
      }
    }
  }
}
